/**
 * One shipping row, two cart surfaces.
 *
 * `/cart` (src/app/[locale]/[channel]/(main)/cart/page.tsx) and the mini-cart
 * (src/ui/components/cart/cart-drawer.tsx) both show a Zwischensumme, a Versand row
 * and a Gesamt, and both derive the Gesamt from `resolveCheckoutGrossTotal`
 * (src/checkout/lib/utils/resolve-checkout-gross-total.ts -- imported by the callers,
 * never edited).
 *
 * The bug this module closes: once the buyer has picked a delivery method in the
 * checkout, `checkout.totalPrice` ALREADY contains `checkout.shippingPrice`. Both
 * surfaces nevertheless added SHIPPING_CONFIG.flatRate on top, so a 69,99 cart with a
 * 9,99 method rendered 79,98 + 9,99 = 89,97. The estimate is only ever a stand-in for a
 * price Saleor cannot compute yet -- the moment Saleor CAN, the estimate has to step
 * aside completely: no addition, no "(Richtwert)" label, and the merchandise line has to
 * give the shipping back that the API total folded in.
 *
 * Deliberately pure and shared so the two surfaces cannot drift apart again; the
 * free-shipping meter keeps running on the merchandise subtotal (`subtotalPrice`, which
 * never contains shipping) and is untouched by any of this.
 */

/** NaN/Infinity in, 0 out -- same guard as src/lib/cart/discount-fold.ts. */
function safeNumber(n: number): number {
	return Number.isFinite(n) ? n : 0;
}

export interface AttachedShipping {
	/** Saleor priced a real delivery method for this checkout. */
	usesAttachedShipping: boolean;
	/** That method's gross price. Always 0 when nothing is attached. */
	amount: number;
}

/**
 * The single place that decides whether a cart is in the "Saleor priced it" regime.
 *
 * Read this BEFORE calling `resolveCheckoutGrossTotal`, not after: that function's
 * unfolded-voucher detection compares `apiTotal` against `subtotal + shipping + tax`,
 * and with a method attached the API total contains the shipping. Handing it a hard
 * `shipping: 0` there made the comparison miss every time, so it returned the raw
 * (undiscounted) API total and the voucher silently vanished from the display --
 * 69,99 Ware + 20 unfolded voucher + 9,99 Versand showed 79,98 instead of 59,98.
 * `amount` is therefore what both that call AND `resolveCartShippingRow` below feed
 * on, so the two can never disagree about which regime the cart is in.
 */
export function resolveAttachedShipping(input: {
	/** `checkout.delivery` is set. */
	hasDeliveryMethod: boolean;
	/** `checkout.shippingPrice.gross.amount`. Saleor reports 0 when no method is selected. */
	shippingPriceAmount: number | null | undefined;
}): AttachedShipping {
	const amount = Math.max(0, safeNumber(input.shippingPriceAmount ?? 0));
	// `shippingPrice > 0` on its own is enough: Saleor can only price shipping for a method
	// it holds, and a checkout carrying a price but no readable `delivery` (a permission-
	// trimmed read, a partial payload) must still not be double-charged in the UI.
	const usesAttachedShipping = input.hasDeliveryMethod || amount > 0;
	return { usesAttachedShipping, amount: usesAttachedShipping ? amount : 0 };
}

export interface CartShippingRowInput {
	/** From `resolveAttachedShipping`, the SAME value that fed `resolveCheckoutGrossTotal`. */
	attached: AttachedShipping;
	/**
	 * `resolveCheckoutGrossTotal(...)` output. Contains the real shipping exactly when a
	 * delivery method is attached -- that is the whole reason this module exists.
	 */
	resolvedTotal: number;
	/** The free-shipping meter's verdict, on the merchandise basis (never includes shipping). */
	freeShippingQualified: boolean;
	/** SHIPPING_CONFIG.flatRate for the channel -- the pre-checkout stand-in. */
	flatRateAmount: number;
}

export interface CartShippingRow {
	/** Saleor's own price is in play; the flat-rate estimate is out of the picture. */
	usesAttachedShipping: boolean;
	/** Render the "Versand (Richtwert)" label instead of the plain "Versand" one. */
	isEstimate: boolean;
	/** Render the existing "Kostenlos" value instead of an amount. */
	isFree: boolean;
	/** The amount the Versand row shows (0 whenever `isFree`). */
	shippingAmount: number;
	/**
	 * Merchandise side of the reconciled total, i.e. the Zwischensumme for surfaces that
	 * derive it from the total rather than from `subtotalPrice`. Equals `resolvedTotal`
	 * while no method is attached, so nothing changes on that path.
	 */
	merchandiseTotal: number;
	/** The Gesamt row. Never adds anything on top of a total that already includes shipping. */
	grandTotal: number;
}

/**
 * Resolves the Versand row and the Gesamt for both cart surfaces.
 *
 * Without an attached delivery method the result is byte-for-byte today's behaviour:
 * free above the threshold, otherwise the flat-rate estimate ADDED to the resolved total.
 */
export function resolveCartShippingRow(input: CartShippingRowInput): CartShippingRow {
	const resolvedTotal = safeNumber(input.resolvedTotal);
	const attachedShippingAmount = Math.max(0, safeNumber(input.attached.amount));
	const usesAttachedShipping = input.attached.usesAttachedShipping;

	if (usesAttachedShipping) {
		return {
			usesAttachedShipping: true,
			// Nothing estimated here -- this IS the price the buyer will be charged.
			isEstimate: false,
			isFree: attachedShippingAmount <= 0,
			shippingAmount: attachedShippingAmount,
			// Give the shipping back that the API total folded in, so Zwischensumme + Versand
			// still add up to Gesamt instead of counting the shipping twice.
			merchandiseTotal: Math.max(0, resolvedTotal - attachedShippingAmount),
			grandTotal: resolvedTotal,
		};
	}

	const estimatedShippingAmount = input.freeShippingQualified ? 0 : safeNumber(input.flatRateAmount);
	return {
		usesAttachedShipping: false,
		isEstimate: !input.freeShippingQualified,
		isFree: input.freeShippingQualified,
		shippingAmount: estimatedShippingAmount,
		merchandiseTotal: resolvedTotal,
		grandTotal: resolvedTotal + estimatedShippingAmount,
	};
}
